import { motion } from "framer-motion";
import { Phone, MapPin, Clock } from "lucide-react";

export default function Booking() {
  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-28 md:py-40 bg-ink text-porcelain overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(circle_at_20%_20%,#F5F4F0_0,transparent_50%)]" />

      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16 grid grid-cols-12 gap-6 md:gap-16">
        {/* Left: heading + massive phone number */}
        <div id="visit" className="col-span-12 lg:col-span-7">
          <div className="flex items-center gap-4 mb-10">
            <span className="w-10 h-px bg-porcelain" />
            <span className="eyebrow !text-porcelain/60">Reserve · By Phone</span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-porcelain text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            data-testid="booking-heading"
          >
            One call. <br />
            <span className="italic" style={{ color: "#B8944E" }}>One clean smile.</span>
          </motion.h2>

          <p className="mt-10 text-porcelain/70 text-base md:text-lg leading-relaxed max-w-[46ch] font-light">
            No forms, no queues. Ring the clinic and we&apos;ll walk you through timings, the
            procedure and pricing — before you set foot inside.
          </p>

          <motion.a
            href="tel:+919961717181"
            data-testid="booking-phone-cta"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
            className="group mt-14 inline-flex items-center gap-6 border-b border-porcelain/30 pb-6 hover:border-taupe transition-colors duration-500"
          >
            <Phone size={28} className="text-taupe shrink-0 group-hover:rotate-[8deg] transition-transform duration-500" />
            <span
              className="text-[10vw] md:text-[6vw] lg:text-[5vw] leading-none tracking-tight text-porcelain group-hover:text-taupe transition-colors duration-500"
              style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}
            >
              +91 99617 17181
            </span>
          </motion.a>

          <div className="mt-6 eyebrow !text-porcelain/50">Tap to call · Mon – Sat · 9:30 AM – 6:00 PM</div>
        </div>

        {/* Right: address + hours as editorial info card */}
        <div className="col-span-12 lg:col-span-5 lg:pl-10 flex flex-col justify-end">
          <div className="border-t border-porcelain/20 pt-10 space-y-10">
            <div className="flex items-start gap-5" data-testid="contact-address">
              <MapPin size={18} className="mt-1 text-taupe shrink-0" />
              <div>
                <div className="eyebrow !text-porcelain/50 mb-2">Visit</div>
                <a
                  href="https://maps.app.goo.gl/abxZS66jBguHm6Ru6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-xl leading-snug block hover:text-taupe transition-colors duration-500"
                  data-testid="google-maps-link"
                >
                  Ground Floor, Kodanchery Arcade,<br />
                  Edamuttam Kattoor Road,<br />
                  Edamuttam · Kerala
                </a>
                <a
                  href="https://maps.app.goo.gl/abxZS66jBguHm6Ru6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 eyebrow !text-porcelain/60 hover:!text-porcelain link-underline"
                  data-testid="get-directions-cta"
                >
                  <span>Get Directions</span>
                  <span aria-hidden>→</span>
                </a>
              </div>
            </div>

            <div className="flex items-start gap-5" data-testid="contact-hours">
              <Clock size={18} className="mt-1 text-taupe shrink-0" />
              <div>
                <div className="eyebrow !text-porcelain/50 mb-2">Hours</div>
                <div className="font-serif text-xl leading-snug">
                  Mon – Sat · 9:30 AM – 6:00 PM<br />
                  <span className="text-porcelain/60">Sunday · Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
